import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Loader2, CalendarDays, Clock, Users, MapPin, Trash2, Send, Eye, Zap, Pencil, Lock } from "lucide-react";

const STATUS_STYLE = {
  DRAFT: "bg-slate-100 text-slate-700",
  PENDING_COACH_APPROVAL: "bg-amber-100 text-amber-700",
  APPROVED: "bg-blue-100 text-blue-700",
  ACTIVE: "bg-green-100 text-green-700",
  CANCELLED: "bg-red-100 text-red-700",
};
const STATUS_LABEL = {
  DRAFT: "Saved",
  PENDING_COACH_APPROVAL: "Awaiting Coach",
  APPROVED: "Coach Approved",
  ACTIVE: "Published",
  CANCELLED: "Cancelled",
};

function isPast(dateStr) {
  if (!dateStr) return false;
  const today = new Date().toISOString().slice(0, 10);
  return dateStr < today;
}

export default function ProviderEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activatingId, setActivatingId] = useState(null);
  const navigate = useNavigate();

  const fetchEvents = async () => {
    try { const res = await api.get("/provider/events"); setEvents(res.data); } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchEvents(); }, []);

  const handleActivate = async (eventId) => {
    setActivatingId(eventId);
    try {
      await api.post(`/events/${eventId}/activate`);
      toast.success("Event published!");
      fetchEvents();
    } catch (err) { toast.error(err.response?.data?.detail || "Cannot publish"); }
    setActivatingId(null);
  };

  const handleCancel = async (eventId) => {
    try {
      await api.delete(`/events/${eventId}`);
      toast.success("Event cancelled");
      fetchEvents();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[300px]"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3">
            <Zap className="w-7 h-7 text-court-orange" />
            <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight" data-testid="events-title">My Events</h1>
          </div>
          <p className="text-muted-foreground mt-1">Organize and manage your venue events</p>
        </div>
        <Button data-testid="organize-event-btn" onClick={() => navigate("/provider/events/create")}
          className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase text-xs px-6 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
          <Plus className="w-4 h-4 mr-1" />Organize Event
        </Button>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-20 bg-white border-2 border-dashed border-slate-200">
          <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="font-heading text-lg text-muted-foreground uppercase">No events yet</p>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Create your first event to get started</p>
          <Button onClick={() => navigate("/provider/events/create")}
            className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase text-xs px-8 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
            <Plus className="w-4 h-4 mr-1" />Organize Event
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map(e => {
            const past = isPast(e.event_date);
            const canEdit = !past && e.status !== "CANCELLED";
            const canPublish = !past && (e.status === "APPROVED" || (e.status === "DRAFT" && !e.coach_id));
            return (
              <div key={e.event_id} className={`bg-white border-2 p-5 transition-colors ${past ? 'border-slate-100 opacity-70' : 'border-slate-100 hover:border-slate-200'}`} data-testid={`event-${e.event_id}`}>
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading text-base font-bold uppercase tracking-tight">{e.title}</h3>
                      <Badge className={`${STATUS_STYLE[e.status] || ''} text-[10px] font-heading uppercase`}>
                        {STATUS_LABEL[e.status] || e.status}
                      </Badge>
                      {e.event_type && <Badge variant="outline" className="text-[10px] font-heading uppercase">{e.event_type}</Badge>}
                      {past && <Badge className="bg-slate-200 text-slate-500 text-[9px] font-heading uppercase">Past</Badge>}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5 text-court-orange" />{e.event_date}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{e.start_time} - {e.end_time}</span>
                      <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{e.registrations_count || 0}/{e.capacity}</span>
                      {e.venue_name && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{e.venue_name}</span>}
                    </div>
                    {e.coach_name && (
                      <div className="text-xs text-muted-foreground">
                        Coach: <strong className="text-foreground">{e.coach_name}</strong>
                        {e.coach_approval_status === "pending" && <Badge className="ml-2 bg-amber-100 text-amber-700 text-[9px]">Awaiting Approval</Badge>}
                        {e.coach_approval_status === "approved" && <Badge className="ml-2 bg-green-100 text-green-700 text-[9px]">Approved</Badge>}
                        {e.coach_approval_status === "declined" && <Badge className="ml-2 bg-red-100 text-red-700 text-[9px]">Declined</Badge>}
                      </div>
                    )}
                    {e.coach_offer && (e.coach_offer.rate_per_hour || e.coach_offer.revenue_pct) && (
                      <p className="text-[10px] text-muted-foreground">
                        Offer: {e.coach_offer.rate_per_hour ? ` ₱${e.coach_offer.rate_per_hour}/hr` : ''}
                        {e.coach_offer.revenue_pct ? ` + ${e.coach_offer.revenue_pct}% revenue` : ''}
                        {e.coach_offer.rating_bonus ? ` + ₱${e.coach_offer.rating_bonus} bonus` : ''}
                      </p>
                    )}
                    {e.space_name && <p className="text-xs text-muted-foreground">Space: {e.space_name}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap shrink-0">
                    {e.registration_type === "paid" && (
                      <span className="font-heading text-lg font-bold text-court-orange">{"\u20B1"}{e.price}</span>
                    )}
                    {e.registration_type === "free" && (
                      <Badge className="bg-green-100 text-green-700 text-xs font-heading uppercase">Free</Badge>
                    )}
                    {canEdit && (
                      <Button data-testid={`edit-btn-${e.event_id}`} size="sm" variant="outline"
                        onClick={() => navigate(`/provider/events/${e.event_id}/edit`)}
                        className="rounded-full text-xs border-2">
                        <Pencil className="w-3 h-3 mr-1" />Edit
                      </Button>
                    )}
                    {past && e.status !== "CANCELLED" && (
                      <Button data-testid={`view-btn-${e.event_id}`} size="sm" variant="outline"
                        onClick={() => navigate(`/provider/events/${e.event_id}/edit?readonly=true`)}
                        className="rounded-full text-xs border-2">
                        <Eye className="w-3 h-3 mr-1" />View
                      </Button>
                    )}
                    {canPublish && (
                      <Button data-testid={`publish-btn-${e.event_id}`} size="sm" onClick={() => handleActivate(e.event_id)}
                        disabled={activatingId === e.event_id}
                        className="bg-green-600 hover:bg-green-700 text-white rounded-full font-heading uppercase text-xs">
                        {activatingId === e.event_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                        Publish
                      </Button>
                    )}
                    {e.status === "ACTIVE" && !past && (
                      <Button size="sm" variant="outline" onClick={() => navigate(`/events/${e.event_id}`)}
                        className="rounded-full text-xs border-2"><Eye className="w-3 h-3 mr-1" />Public View</Button>
                    )}
                    {canEdit && e.status !== "ACTIVE" && (
                      <Button size="sm" variant="outline" onClick={() => handleCancel(e.event_id)}
                        className="rounded-full text-xs border-2 text-red-500 border-red-200 hover:bg-red-50">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
